import { useReducedMotion } from 'framer-motion';
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import type { BizyPeopleBrowserEntry } from '../../../lib/bizy/types';
import { useReduceBizyMotionPreference } from '../../bizy';
import {
	BIZY_DECK_GESTURE_DURATION_MS,
	BIZY_DECK_GESTURE_TRANSITION,
	DECK_INSTANT,
	DECK_PLACEHOLDER_KEY,
	DECK_TRANSITION,
	getDeckBizyState,
	type DeckAnimationMeta,
	type DeckTransitionRegistry,
	type SwipeStart,
} from '../lib/bizy-deck-animation';
import { deckPose, deckTransitionDirection, type DeckDirection } from '../lib/bizy-deck-geometry';

const COMPACT_SWIPE_THRESHOLD_PX = 48;

interface UseBizyMomentDeckParams {
	activeObservedMomentId: string | undefined;
	viewedPerson: { id: string; name: string; observedMomentId: string } | null;
	currentIndex: number;
	snapshot: BizyPeopleBrowserEntry[];
	viewPerson: (person: { id: string; name: string; observedMomentId: string }) => void;
	isCompact: boolean;
	rootRef: RefObject<HTMLDivElement>;
}

/**
 * The moment deck's state machine (BIZY-665/667): hydration-key resolution,
 * reduced-motion-aware timing, the peel/settle gesture flag, the transition
 * registry that keeps in-flight exits on their captured target, focus hand-off
 * after paging, and pointer-swipe paging. The person view supplies the current
 * position and consumes a presentational bundle — it carries none of this
 * bookkeeping itself.
 */
export function useBizyMomentDeck({ activeObservedMomentId, viewedPerson, currentIndex, snapshot, viewPerson, isCompact, rootRef }: UseBizyMomentDeckParams) {
	// Which way the deck is being paged, so the card animation matches the intent.
	const requestedDeckDirectionRef = useRef<DeckDirection>(1);
	const prefersReducedMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();
	const deckTransition = prefersReducedMotion || reduceBizyMotion ? DECK_INSTANT : DECK_TRANSITION;
	const bizyGestureTransition = prefersReducedMotion || reduceBizyMotion ? DECK_INSTANT : BIZY_DECK_GESTURE_TRANSITION;
	const [isDeckGestureActive, setIsDeckGestureActive] = useState(false);
	const previousGestureMomentRef = useRef(activeObservedMomentId);
	useLayoutEffect(() => {
		const previousMomentId = previousGestureMomentRef.current;
		previousGestureMomentRef.current = activeObservedMomentId;
		if (!previousMomentId || !activeObservedMomentId || previousMomentId === activeObservedMomentId) return;

		setIsDeckGestureActive(true);
		const timeoutId = window.setTimeout(() => setIsDeckGestureActive(false), BIZY_DECK_GESTURE_DURATION_MS);
		return () => window.clearTimeout(timeoutId);
	}, [activeObservedMomentId]);

	const logicalDeckKey = activeObservedMomentId || DECK_PLACEHOLDER_KEY;
	const hydrationKeyRef = useRef<{ personId: string; momentId?: string; key: string }>();
	const hydrationPersonId = viewedPerson?.id;

	// Resolve the card's React key during render (never in an effect): keep the
	// same key while a deep link fills in its first real moment. The placeholder
	// content becomes the card in place, so there is no outgoing skeleton to
	// animate or briefly layer over the hydrated card.
	const resolveDeckKey = (): string => {
		if (!activeObservedMomentId && hydrationPersonId) {
			if (hydrationKeyRef.current?.personId !== hydrationPersonId) {
				hydrationKeyRef.current = {
					personId: hydrationPersonId,
					key: `${DECK_PLACEHOLDER_KEY}:${hydrationPersonId}`,
				};
			}
		} else if (
			activeObservedMomentId &&
			hydrationPersonId &&
			hydrationKeyRef.current?.personId === hydrationPersonId &&
			hydrationKeyRef.current.momentId == null
		) {
			hydrationKeyRef.current.momentId = activeObservedMomentId;
		}

		return hydrationPersonId != null &&
			hydrationKeyRef.current?.personId === hydrationPersonId &&
			(!activeObservedMomentId || hydrationKeyRef.current.momentId === activeObservedMomentId)
			? hydrationKeyRef.current.key
			: logicalDeckKey;
	};
	const deckKey = resolveDeckKey();

	// Which paging control the user pressed, if any. The control stays mounted
	// outside the keyed card, but paging onto an end can disable it; hand focus
	// to the usable counterpart rather than leaving it on a disabled button.
	const pendingNavFocusRef = useRef<'prev' | 'next' | null>(null);
	const swipeStartRef = useRef<SwipeStart | null>(null);

	// Focus the view whenever a card opens — the click that opened it (a
	// sidebar entry, including reselects while the view stays mounted in
	// fullscreen) leaves focus outside the root, which the guard below would
	// treat as "aimed elsewhere", so arrows wouldn't page until a first click
	// inside the card (BIZY-671).
	useEffect(() => {
		const pending = pendingNavFocusRef.current;
		pendingNavFocusRef.current = null;

		if (pending) {
			// Prefer the control just pressed. Paging onto the last card disables
			// it, so fall to its counterpart and then the primary action rather
			// than dumping focus on the unlabelled root mid-interaction.
			const preferred = pending === 'next' ? ['next', 'prev'] : ['prev', 'next'];
			const candidates = [...preferred.map((nav) => `[data-deck-nav="${nav}"]`), '[data-deck-action="draft"]'];

			for (const selector of candidates) {
				const button = rootRef.current?.querySelector<HTMLButtonElement>(selector);
				if (button && !button.disabled) {
					button.focus({ preventScroll: true });
					return;
				}
			}
		}

		rootRef.current?.focus({ preventScroll: true });
	}, [activeObservedMomentId, rootRef]);

	const lastCommittedDeckRef = useRef({ key: deckKey, index: currentIndex, direction: requestedDeckDirectionRef.current, depth: 1 });
	const transitionRegistryRef = useRef<DeckTransitionRegistry>({});
	const previousDeck = lastCommittedDeckRef.current;
	const didDeckKeyChange = previousDeck.key !== deckKey;
	const deckDirection = didDeckKeyChange
		? deckTransitionDirection(previousDeck.index, currentIndex, requestedDeckDirectionRef.current)
		: previousDeck.direction;
	const deckDistance =
		didDeckKeyChange && previousDeck.index >= 0 && currentIndex >= 0 ? Math.max(1, Math.abs(currentIndex - previousDeck.index)) : previousDeck.depth;
	const currentDeckAnimation: DeckAnimationMeta = {
		direction: deckDirection,
		pose: deckPose(activeObservedMomentId || DECK_PLACEHOLDER_KEY, deckDistance),
		exitPose: deckPose(previousDeck.key || DECK_PLACEHOLDER_KEY, deckDistance),
		isCompact,
		transition: deckTransition,
	};
	const deckBizyState = getDeckBizyState(isCompact, isDeckGestureActive);
	const transitionRegistry = didDeckKeyChange ? { ...transitionRegistryRef.current, [previousDeck.key]: currentDeckAnimation } : transitionRegistryRef.current;

	useLayoutEffect(() => {
		lastCommittedDeckRef.current = { key: deckKey, index: currentIndex, direction: deckDirection, depth: deckDistance };
		transitionRegistryRef.current = transitionRegistry;
	}, [currentIndex, deckDirection, deckDistance, deckKey, transitionRegistry]);

	const pageDeck = (direction: DeckDirection, restoreControlFocus: boolean) => {
		const entry = snapshot[currentIndex + direction];
		if (!entry) return false;
		pendingNavFocusRef.current = restoreControlFocus ? (direction === -1 ? 'prev' : 'next') : null;
		requestedDeckDirectionRef.current = direction;
		viewPerson({ id: entry.person.id, name: entry.person.name ?? '', observedMomentId: entry.observedMomentId });
		return true;
	};

	const handleCardPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (!isCompact || event.pointerType === 'mouse' || event.button !== 0) return;
		const target = event.target;
		if (target instanceof Element && target.closest('button, a, input, textarea, select, [role="button"]')) return;

		swipeStartRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
		event.currentTarget.setPointerCapture?.(event.pointerId);
	};

	const handleCardPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
		const start = swipeStartRef.current;
		if (!start || start.pointerId !== event.pointerId) return;
		swipeStartRef.current = null;
		if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture?.(event.pointerId);

		const deltaX = event.clientX - start.x;
		const deltaY = event.clientY - start.y;
		if (Math.abs(deltaX) < COMPACT_SWIPE_THRESHOLD_PX || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
		pageDeck(deltaX < 0 ? 1 : -1, false);
	};

	const handleCardPointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
		if (swipeStartRef.current?.pointerId === event.pointerId) swipeStartRef.current = null;
	};

	return {
		deckKey,
		logicalDeckKey,
		deckDistance,
		currentDeckAnimation,
		transitionRegistry,
		transitionRegistryRef,
		deckBizyState,
		isDeckGestureActive,
		deckTransition,
		bizyGestureTransition,
		requestedDeckDirectionRef,
		pageDeck,
		handleCardPointerDown,
		handleCardPointerUp,
		handleCardPointerCancel,
	};
}
